<template>
  <div class="my-signatures h-100 d-flex flex-column px-4" data-locator="signed-documents-page">
    <SignedDocumentsHeader 
      v-model:selected-signed-documents="selectedSignedDocuments"
      v-model:display="display"
      :no-data="hasNoData"
      :no-results="hasNoResults"
      :disabled="hasNoData || hasNoResults || loading"
      :areAllSelectedInCurrentPage="areAllSelectedInCurrentPage"
      :areSomeSelectedInCurrentPage="areSomeSelectedInCurrentPage"
      @selectedSignature="selectedSignedDocuments = $event"
      @toggle-all-selection="toggleGlobalSelection"
    />
    
    <template v-if="hasNoData">
      <app-empty-state
        class="mt-16"
        module="signed-documents"
        size="sm"
        :translation-key-prefix="TRANSLATION_KEY_PREFIX"
      />
    </template>
    <template v-else-if="hasNoResults">
      <div class="d-flex flex-column justify-center align-center mt-16">
        <img src="@/assets/img/projet_signature.svg" alt="" width="300">
        <div class="my-5 text-center">
          {{$t(`${TRANSLATION_KEY_PREFIX}.hasNoResults`)}}
        </div>
        <app-button
          @click="deleteSearchAndFilters"
        >
          {{ $t(`${TRANSLATION_KEY_PREFIX}.hasNoResultsButton`) }}
        </app-button>
      </div>
    </template>
    <template v-else>
      <div class="mt-6">
        <template v-if="display === DISPLAY.grid">
          <app-grid
            :is-loading="loading && filteredSignedDocuments.length === 0 || !fetched"
            :items-count="filteredSignedDocuments.length"
          >
            <template
              v-for="signedDocument in paginatedDocuments"
              :key="signedDocument.id"
            >
              <SignedDocumentsGridItem 
                :signed-document="signedDocument"
              />
            </template>
          </app-grid>
        </template>
        <template v-if="display === DISPLAY.table">
          <SignedDocumentsTable
            :signedDocuments="filteredSignedDocuments"
            :currentPage="currentPage"
            :itemsPerPage="itemsPerPage"
            :areAllSelectedInCurrentPage="areAllSelectedInCurrentPage"
            :areSomeSelectedInCurrentPage="areSomeSelectedInCurrentPage"
            @toggle-all-selection="toggleGlobalSelection"
          />
        </template>
      </div>
    </template>

    <template v-if="displayPagination && !loading">
      <div class="pagination">
        <AppPagination
          v-model:current-page="currentPage"
          v-model:page-size="itemsPerPage"
          :total="filteredSignedDocuments.length"
          :page-sizes="[25, 50, 100]"
        />
      </div>
    </template>
    
    <SharePane 
      v-model:model-value="shareOriginals.isOpen"
      v-model:documents="shareOriginals.documents"
      @removeDocumentToShare="removeDocumentToShare"
    />

    <DeleteSignedDocumentsDialog 
      v-if="deleteOriginals.isOpen"
      :documents="deleteOriginals.documents"
      @close="closeDeleteOriginalsDialog"
      @delete-success="selectedSignedDocuments = []"
    />

    <SignedDocumentsSignatoriesListDialog 
      v-if="signatoriesListModalSignedDocument"
      :mnemo="roomMnemo"
      :signed-document="signatoriesListModalSignedDocument"
      @close="signatoriesListModalSignedDocument = null"
    />

    <IManageFolderPickerDialog 
      v-if="exportToIManage.isOpen"
      v-model:is-open="exportToIManage.isOpen"
      @folders="prepareExportSignedDocumentsToImanage"
    />

    <PolyOfficeDialog 
      v-if="polyOfficeEnabled && dialogIsOpen"
      @callback="polyOfficeDialogCallback"
    />
  </div>
</template>

<script>
import { computed } from 'vue'
import { mapState, mapActions, mapGetters, useStore } from 'vuex'

import IManageFolderPickerDialog from '@/common/imanage/IManageFolderPickerDialog.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import PolyOfficeDialog, { POLY_OFFICE_DIALOG_STATUS_SUCCESS } from '@/common/polyOffice/PolyOfficeDialog.vue'
import { toast } from '@/common/utils/toast'
import DeleteSignedDocumentsDialog from '@/project/signed-documents/dialogs/DeleteSignedDocumentsDialog.vue'
import SharePane from '@/project/signed-documents/pane/SharePane.vue'
import SignedDocumentsGridItem from '@/project/signed-documents/SignedDocumentsGridItem.vue'
import SignedDocumentsHeader from '@/project/signed-documents/SignedDocumentsHeader.vue'
import SignedDocumentsSignatoriesListDialog from '@/project/signed-documents/SignedDocumentsSignatoriesListDialog.vue'
import SignedDocumentsTable from '@/project/signed-documents/SignedDocumentsTable.vue'
import { DISPLAY } from '@/projects/display'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { EXPORT_SIGNED_DOCUMENT_TO_IMANAGE, EXPORT_SIGNED_DOCUMENTS_TO_IMANAGE, GET_SIGNED_DOCUMENTS } from '@/store/modules/signed-documents/action_types'

const TRANSLATION_KEY_PREFIX = 'project.signed-documents.SignedDocuments'

export default {
  name: 'SignedDocuments',
  components: {
    AppPagination,
    DeleteSignedDocumentsDialog,
    IManageFolderPickerDialog,
    PolyOfficeDialog,
    SharePane,
    SignedDocumentsHeader,
    SignedDocumentsGridItem,
    SignedDocumentsSignatoriesListDialog,
    SignedDocumentsTable,
  },
  provide () {
    return {
      selectedSignedDocuments: computed(() => this.selectedSignedDocuments),
      isSignedDocumentSelected: (signedDocument) => {
        return !!this.selectedSignedDocuments.find(document => document.id === signedDocument.id)
      },
      toggleSignedDocumentSelection: (signedDocument) => {
        const wasDocSelected = this.selectedSignedDocuments.some(doc => doc.id === signedDocument.id)
        if (wasDocSelected) {
          this.selectedSignedDocuments = this.selectedSignedDocuments.filter(doc => doc.id !== signedDocument.id)
        } else {
          this.selectedSignedDocuments = [...this.selectedSignedDocuments, { ...signedDocument }]
        }
      },
      unselectAllSignedDocuments: () => {
        this.selectedSignedDocuments = []
      },
      openSignatoriesListDialog: (document) => {
        this.signatoriesListModalSignedDocument = document
      },
      openDeleteOriginalsDialog: (documents) => {
        this.deleteOriginals = {
          isOpen: true,
          documents: documents,
        }
      },
      openExportToIManageDialog: (document) => {
        this.exportToIManage = {
          isOpen: true,
          document: document,
        }
      },
      openShareOriginalsPane: this.onShareOriginal,
    }
  },
  setup: () => {
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: 'signed-documents-items-per-page',
    })

    return {
      currentPage,
      itemsPerPage,
    }
  },
  data () {
    return {
      DISPLAY,
      TRANSLATION_KEY_PREFIX,
      display: DISPLAY.grid,
      deleteOriginals: {
        documents: [],
        isOpen: false,
      },
      exportToIManage: {
        document: null,
        isOpen: false,
      },
      fetched: false,
      selectedSignedDocuments: [],
      shareOriginals: {
        documents: [],
        isOpen: false,
      },
      signatoriesListModalSignedDocument: null,
    }
  },
  head () {
    return {
      title: this.$t(`${TRANSLATION_KEY_PREFIX}.metaTitle`),
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapGetters('signedDocuments', ['filteredSignedDocuments', 'signedDocumentsToDisplay']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    ...mapState('groups', ['groups']),
    ...mapState('polyOffice', ['dialogIsOpen', 'dialogSuccess']),
    ...mapState('signedDocuments', ['loading']),
    hasNoData () {
      return this.signedDocumentsToDisplay.length === 0 && this.fetched
    },
    hasNoResults () {
      return this.filteredSignedDocuments.length === 0 && this.signedDocumentsToDisplay.length > 0
    },
    paginatedDocuments () {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage

      return this.filteredSignedDocuments.slice(start, end)
    },
    displayPagination () {
      return !this.loading && !this.hasNoData && !this.hasNoResults
    },
    areAllSelectedInCurrentPage () {
      return this.paginatedDocuments.length > 0 &&
        this.paginatedDocuments.filter(doc => this.checkIfDocumentIsSelectable(doc)).every(doc =>
          this.selectedSignedDocuments.some(selectedDoc => selectedDoc.id === doc.id),
        )
    },
    areSomeSelectedInCurrentPage () {
      return this.paginatedDocuments.some(doc =>
        this.selectedSignedDocuments.some(selectedDoc => selectedDoc.id === doc.id),
      ) && !this.areAllSelectedInCurrentPage;
    },
 },
  async mounted () {
    await this.GET_SIGNED_DOCUMENTS({ mnemo: this.roomMnemo, params: {
      fileDetails: true,
      rightsDetails: true,
      signatureDetails: true,
    } })

    this.fetched = true

    this.GET_GROUPS(this.roomMnemo)
  },
  methods: {
    ...mapActions('signedDocuments', [
      GET_SIGNED_DOCUMENTS,
      'setSignDateModel',
      'setFiltersStartDate',
      'setFiltersEndDate',
      'setFiltersSignatories',
      'setSearchQuery',
      EXPORT_SIGNED_DOCUMENT_TO_IMANAGE,
      EXPORT_SIGNED_DOCUMENTS_TO_IMANAGE,
    ]),
    ...mapActions('documentSharePicker', [DocumentSharePickerActionTypes.DISABLE_MEMBERS]),
    ...mapActions('groups', ['GET_GROUPS']),
    onShareOriginal (documents) {
      if (documents.length === 1 && documents[0].rights) {
        if (documents[0].rights.readSigned.groups.length > 0) {
          const membersToDisable = this.groups.filter(group => group.id === documents[0].id)
            .flatMap(group => group.members)

          if (membersToDisable.length) {
            this.DISABLE_MEMBERS(membersToDisable)
          }
        }

        if (documents[0].rights.readSigned.users.length > 0) {
          const membersToDisable = this.groups.flatMap(group => group.members)
            .filter(member => documents[0].rights.readSigned.users.find(signer => signer === member.id))

          if (membersToDisable.length) {
            this.DISABLE_MEMBERS(membersToDisable)
          }
        }
      }

      this.shareOriginals = {
        isOpen: true,
        documents: documents,
      }
    },
    deleteSearchAndFilters () {
      this.setFiltersSignatories([])
      this.setFiltersStartDate(null)
      this.setFiltersEndDate(null)
      this.setSignDateModel([])
      this.setSearchQuery('')
    },
    removeDocumentToShare (document) {
      this.shareOriginals.documents.splice(
        this.shareOriginals.documents.findIndex(o => o.id === document.id),
        1,
      )

      if (this.shareOriginals.documents.length === 0) {
        this.shareOriginals.isOpen = false
      }
    },
    prepareExportSignedDocumentsToImanage (target) {
      if(this.exportToIManage.document.length > 1) {
        this.exportSignedDocumentsToImanage(target)
      } else {
        this.exportSignedDocumentToImanage(target)
      }
    },
    async exportSignedDocumentToImanage(target) {
      try {
        await this.EXPORT_SIGNED_DOCUMENT_TO_IMANAGE({
          mnemo: this.roomMnemo,
          envelopeId: this.exportToIManage.document[0],
          data: {
            targetId: target.selected,
          },
        })
        this.unselectAllInCurrentPage()
        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.exportSignedDocumentToImanageSuccess`))
      } catch(error) {
        console.error(error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.exportSignedDocumentToImanageError`))
      }
    },
    async exportSignedDocumentsToImanage(target) {
      try {
        const response = await this.EXPORT_SIGNED_DOCUMENTS_TO_IMANAGE({
          mnemo: this.roomMnemo,
          envelopesId: this.exportToIManage.document.join(','),
          data: {
            targetId: target.selected,
          },
        })

        const failedExports = response.filter(res => res.status !== 'success')

        if (failedExports.length) {
          toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.exportSignedDocumentsToImanageError`))
        } else {
          this.unselectAllInCurrentPage()
          toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.exportSignedDocumentsToImanageSuccess`))
        }
      } catch(error) {
        console.error(error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.exportSignedDocumentsToImanageError`))
      }
    },
    polyOfficeDialogCallback (data) {
      if (data.status === POLY_OFFICE_DIALOG_STATUS_SUCCESS) {
        toast.success(this.dialogSuccess)
      }
    },
    closeDeleteOriginalsDialog () {
      this.deleteOriginals = {
        isOpen: false,
        documents: [],
      }
    },
    selectAllInCurrentPage () {
      const newSelectedDocuments = this.paginatedDocuments?.filter(doc =>
        !this.selectedSignedDocuments.some(selectedDoc => selectedDoc.id === doc.id) 
        && this.checkIfDocumentIsSelectable(doc),
      )
      this.selectedSignedDocuments = [
        ...this.selectedSignedDocuments,
        ...newSelectedDocuments,
      ]
    },
    unselectAllInCurrentPage () {
      this.selectedSignedDocuments = this.selectedSignedDocuments.filter(selectedDoc =>
        !this.paginatedDocuments?.some(doc => doc.id === selectedDoc.id),
      )
    },
    toggleGlobalSelection () {
      if (!this.areAllSelectedInCurrentPage) {
        this.selectAllInCurrentPage()
      } else {
        this.unselectAllInCurrentPage()
      }
    },
    checkIfDocumentIsSelectable (document) {
      return !!document.proofFileURL || !!document.signedDocumentURL
    },
  },
}
</script>

<style scoped lang="scss">
.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  flex-grow: 1;
  width: 100%;
}
</style>